// One node's content, rendered the same way wherever it appears: in the right
// pane of /graph beside the drawing, and on its own page at /graph/node/:slug.
//
// The difference between the two is only what a link does. With `onSelect` the
// panel is a viewer inside a page that is already showing the graph, so a link
// swaps the pane and the drawing stays put; without it, every link is an
// ordinary route change.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import {
  captionFigures,
  normalizeDisplayMath,
  renderWikiLinks,
} from "../utils/wikiLinks";
import { formatStamp } from "../utils/datetime";
import ConfirmDialog from "./ConfirmDialog";
import graphApi from "../services/graphApi";

/** Play the node read aloud — the SELF-CONTAINED player.
 *
 * A button rather than `<audio controls>`: the native player is a wide,
 * light-themed bar that would sit under the title of every voiced node whether
 * or not anyone wanted it. This is one control that says how long the clip is,
 * and the file is only fetched when it is pressed (`preload="none"`) — a node
 * with a narration costs nothing to open.
 *
 * Used where the panel is the whole page (/graph/node/:slug). On /graph the
 * page owns one long-lived element instead and passes `onTogglePlay` — see
 * `ListenPill` below for why that distinction matters.
 */
function ListenButton({ src, duration }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  // A new node means a new clip: stop whatever was playing rather than letting
  // the previous node keep talking over the one now on screen.
  useEffect(() => {
    setPlaying(false);
    const audio = ref.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  }, [src]);

  const toggle = () => {
    const audio = ref.current;
    if (!audio) return;
    holdRate(audio);
    if (audio.paused) {
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        className="mt-1 flex shrink-0 items-center gap-1.5 rounded-full border border-slate-700 px-3 py-0.5 text-xs text-slate-300 transition hover:border-sky-500/40 hover:text-sky-300"
        aria-label={playing ? "Pause the narration" : "Play the narration"}
      >
        <span aria-hidden="true">{playing ? "❚❚" : "▶"}</span>
        Listen
        {duration && <span className="text-slate-500">{duration}</span>}
      </button>
      <audio
        ref={ref}
        src={src}
        preload="none"
        onLoadedMetadata={(e) => holdRate(e.currentTarget)}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={(e) => {
          holdRate(e.currentTarget);
          setPlaying(true);
        }}
      />
    </>
  );
}

/** The same control, driven from outside.
 *
 * On /graph the audio element lives in the page, not here, because this panel
 * is unmounted and remounted on every node change — and a browser grants
 * permission to play to an ELEMENT, off the gesture that started it. A fresh
 * element per node means a fresh permission per node, which is what stopped
 * autoplay after the first clip. One element that outlives the nodes keeps the
 * permission it was given when Autoplay was pressed.
 */
function ListenPill({ duration, playing, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="mt-1 flex shrink-0 items-center gap-1.5 rounded-full border border-slate-700 px-3 py-0.5 text-xs text-slate-300 transition hover:border-sky-500/40 hover:text-sky-300"
      aria-label={playing ? "Pause the narration" : "Play the narration"}
    >
      <span aria-hidden="true">{playing ? "❚❚" : "▶"}</span>
      Listen
      {duration && <span className="text-slate-500">{duration}</span>}
    </button>
  );
}

/** A link to something outside the graph that teaches this node better.
 *
 * A pill beside Listen rather than a line in the body: it is not part of what
 * the node says, it is where to go if the node did not land. `rel=noreferrer`
 * and a new tab because it leaves the site — losing the reading position to a
 * YouTube lecture is the one thing the reading pane must not do.
 *
 * The host is shown rather than the URL. "youtube.com" tells a reader what they
 * are committing to; a 70-character watch link with a timestamp tells them
 * nothing and wraps over two lines doing it.
 */
function SupplementaryLink({ href }) {
  let host = "";
  try {
    host = new URL(href).hostname.replace(/^www\./, "");
  } catch {
    // Not a URL the browser can parse — show the pill anyway. A malformed
    // `supplementary:` is an authoring mistake, and hiding it is how it stays
    // one; the link simply will not resolve, which is visible.
    host = "";
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="mt-1 flex shrink-0 items-center gap-1.5 rounded-full border border-slate-700 px-3 py-0.5 text-xs text-slate-300 transition hover:border-sky-500/40 hover:text-sky-300"
      title={href}
    >
      <span aria-hidden="true">↗</span>
      Supplementary
      {host && <span className="text-slate-500">{host}</span>}
    </a>
  );
}

// The same rate as the page-level player on /graph — see PLAYBACK_RATE
// there for why. Duplicated rather than shared because the two players are
// deliberately independent: this one exists where there is no page to own it.
// Both must move together: a mismatch is one node sounding different from the
// next depending on which player happened to open it.
const PLAYBACK_RATE = 1.0;
const holdRate = (el) => {
  if (el && el.playbackRate !== PLAYBACK_RATE) el.playbackRate = PLAYBACK_RATE;
};

// KaTeX ships its own fonts and stylesheet; both are bundled by the build, so
// nothing is fetched from a CDN at runtime. remark-math reads $…$ and $$…$$ and
// rehype-katex renders them — without the pair, a node written about matrices
// shows its notation as raw backslashes, which is most of what the maths nodes
// are for.
const MATH_REMARK = [remarkGfm, remarkMath];
const MATH_REHYPE = [rehypeKatex];

const PROSE =
  "prose prose-invert max-w-none prose-p:text-slate-300 prose-li:text-slate-300 " +
  "prose-headings:text-slate-100 prose-strong:text-slate-100 prose-a:text-sky-400 " +
  "prose-code:text-sky-300 prose-pre:bg-slate-900/60 prose-pre:text-slate-200 " +
  "prose-th:text-slate-200 prose-td:text-slate-300";

/** A node reference — a button when selecting in place, a link otherwise. */
function NodeRef({ node, onSelect, className, children }) {
  if (onSelect) {
    return (
      <button type="button" onClick={() => onSelect(node.slug)} className={className}>
        {children}
      </button>
    );
  }
  return (
    <Link to={`/graph/node/${node.slug}`} className={className}>
      {children}
    </Link>
  );
}

function EdgeList({ title, empty, nodes, onSelect }) {
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
        {title}
      </h3>
      {nodes.length === 0 ? (
        <p className="text-sm text-slate-500">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {nodes.map((n) => (
            <li key={n.slug}>
              <NodeRef
                node={n}
                onSelect={onSelect}
                className="flex w-full items-center justify-between gap-2 text-left text-sm text-slate-300 transition hover:text-sky-300"
              >
                <span>{n.title}</span>
              </NodeRef>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const VOTE_FIELDS = [
  { key: "important", label: "Important", hint: "This lesson matters" },
];

/** Vote / unvote for one field, with the log's current position.
 *
 * The log is append-only: unvote does not erase a vote, it records a change of
 * mind. So the buttons show the CURRENT position (the last entry) while the
 * tally shows the history — pressing the button you are already on is allowed
 * and appends another entry, which is what makes "I keep changing my mind about
 * this one" visible at all.
 */
function VoteRow({ field, pressed, pending, failed, onVote }) {
  // What each button actually appends. Inverted fields flip both, so the log
  // stays in the column's own terms whichever way the label is phrased.
  //
  // Both are derived with `!`, never read straight off `field.invert`: a field
  // with no `invert` key gives `undefined`, JSON.stringify DROPS an undefined
  // value, and the request then arrives with no `vote` at all — a 422 that the
  // UI used to swallow in silence.
  const voteValue = !field.invert;
  const unvoteValue = !voteValue;

  const button = (value, text) => {
    // Lit only if YOU pressed it since this node was opened. The vote log is
    // append-only and permanent, but the button is not a display of it: a page
    // that opens with a button already lit is telling you what you once
    // thought, which is both stale and easy to mistake for what you are about
    // to do. Reading a node fresh should ask the question, not pre-answer it.
    const on = pressed === value;
    return (
      <button
        type="button"
        disabled={pending}
        onClick={() => onVote(field.key, value)}
        aria-pressed={on}
        // Pressed Unvote is ROSE, not grey. Grey differed from the unpressed
        // state by a shade of border, so pressing it looked like nothing had
        // happened — and rose matches the "No" button directly above, where the
        // same colour already means the same thing.
        className={`rounded-lg border px-3 py-1 text-xs transition disabled:opacity-50 ${
          on
            ? value
              ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
              : "border-rose-500/50 bg-rose-500/15 text-rose-300"
            : "border-slate-800 bg-slate-900/40 text-slate-400 hover:border-sky-500/40 hover:text-sky-300"
        }`}
      >
        {text}
      </button>
    );
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-1.5">
      <div>
        <span className="text-sm text-slate-200">{field.label}</span>
        <span className="ml-2 text-xs text-slate-500">{field.hint}</span>
      </div>
      <div className="flex items-center gap-2">
        {failed && (
          <span className="text-[11px] text-rose-400">not saved</span>
        )}
        {button(voteValue, "Vote")}
        {button(unvoteValue, "Unvote")}
      </div>
    </div>
  );
}

export default function NodePanel({
  node,
  known,
  onSelect = null,
  compact = false,
  onDeleted = null,
  // Set when the PAGE owns the audio element (see ListenPill). Without them the
  // panel plays the clip itself.
  playing = false,
  onTogglePlay = null,
  // Reading navigation, rendered at the foot of the node beside Delete. Passed
  // in rather than built here: NodePanel knows one node, and where to go next
  // is a question about the whole graph.
  nav = null,
}) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletedAt, setDeletedAt] = useState(null);
  // What you pressed while this node has been open — NOT the stored log. Reset
  // whenever the node changes, so every node (and every fresh visit to one)
  // starts with nothing lit.
  const [pressed, setPressed] = useState({});
  const [pending, setPending] = useState(null);
  const [failed, setFailed] = useState(null);

  useEffect(() => {
    setPressed({});
    setFailed(null);
    setConfirming(false);
    setDeletedAt(node?.deleted_at || null);
  }, [node?.slug, node?.deleted_at]);

  const removeNode = async () => {
    if (!node) return;
    setDeleting(true);
    try {
      const result = await graphApi.deleteNode(node.slug);
      setDeletedAt(result.deleted_at);
      setConfirming(false);
      if (onDeleted) onDeleted(node.slug);
    } catch {
      setConfirming(false);
    } finally {
      setDeleting(false);
    }
  };

  const restoreNode = async () => {
    if (!node) return;
    setDeleting(true);
    try {
      const result = await graphApi.restoreNode(node.slug);
      setDeletedAt(result.deleted_at);
    } catch {
      /* leave the banner up — nothing changed */
    } finally {
      setDeleting(false);
    }
  };

  const castVote = async (field, value) => {
    if (!node) return;
    setPending(field);
    setFailed(null);
    try {
      await graphApi.vote(node.slug, field, value);
      setPressed((current) => ({ ...current, [field]: value }));
    } catch (err) {
      // Leave the log as it was — showing a vote the server never recorded is
      // worse than showing none — but SAY so. Swallowing this hid a 422 for a
      // whole round of testing: the button looked like it worked and nothing
      // was ever saved.
      setFailed(field);
    } finally {
      setPending(null);
    }
  };

  // Links first, then the display-math layout, then the figure captions.
  // Equations are deliberately not numbered — see wikiLinks.
  const body = useMemo(() => {
    if (!node) return "";
    const linked = renderWikiLinks(node.content, known);
    return captionFigures(normalizeDisplayMath(linked), node.id);
  }, [node, known]);

  // A [[slug]] inside the prose became /graph/node/<slug> in wikiLinks. When the
  // panel selects in place, catch those and swap the pane instead of routing;
  // anything else (an external URL, an anchor) is left alone.
  const components = useMemo(() => {
    if (!onSelect) return undefined;
    return {
      a: ({ href, children, ...rest }) => {
        const match = /^\/graph\/node\/(.+)$/.exec(href || "");
        if (!match) {
          return (
            <a href={href} {...rest}>
              {children}
            </a>
          );
        }
        return (
          <button
            type="button"
            onClick={() => onSelect(match[1])}
            className="text-sky-400 underline underline-offset-2 transition hover:text-sky-300"
          >
            {children}
          </button>
        );
      },
    };
  }, [onSelect]);

  if (!node) return null;

  return (
    <article>
      <div className="flex flex-wrap items-start gap-3">
        <h1
          className={`font-bold tracking-tight text-slate-100 ${
            compact ? "text-xl" : "text-3xl"
          }`}
        >
          {node.title}
        </h1>
        {/* The subjects the node belongs to. A node can carry several; they say
            what it is about. */}
        {node.audio &&
          (onTogglePlay ? (
            <ListenPill
              duration={node.audio_duration}
              playing={playing}
              onToggle={onTogglePlay}
            />
          ) : (
            <ListenButton src={node.audio} duration={node.audio_duration} />
          ))}
        {node.supplementary && <SupplementaryLink href={node.supplementary} />}
        {(node.labels || []).map((label) => (
          <span
            key={label}
            className="mt-1 shrink-0 rounded-full border border-slate-700 px-2.5 py-0.5 text-xs text-slate-400"
          >
            {label}
          </span>
        ))}
      </div>

      {node.source && (
        <p className="mt-2 break-words text-xs text-slate-500">
          Seeded from {node.source}
        </p>
      )}

      <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
        <div className="flex gap-1.5">
          <dt>Created</dt>
          <dd className="text-slate-400">
            <time dateTime={node.created_at || undefined}>
              {formatStamp(node.created_at)}
            </time>
          </dd>
        </div>
        <div className="flex gap-1.5">
          <dt>Modified</dt>
          <dd className="text-slate-400">
            <time dateTime={node.updated_at || undefined}>
              {formatStamp(node.updated_at)}
            </time>
          </dd>
        </div>
        {/* The open log's payoff: not "you read this N times" but the date the
            schedule puts the next review on. Overdue is said plainly. */}
        {node.due_at && (
          <div className="flex gap-1.5">
            <dt>{new Date(node.due_at) <= new Date() ? "Due now" : "Next review"}</dt>
            <dd
              className={
                new Date(node.due_at) <= new Date() ? "text-amber-400" : "text-slate-400"
              }
            >
              <time dateTime={node.due_at}>{formatStamp(node.due_at)}</time>
              {/* Counted readings, not opens: the first meeting plus every
                  review the schedule asked for. A long autoplay run puts a node
                  back on screen when nothing is due, and those hearings earn no
                  rung on the ladder — saying "7 readings" for them would claim
                  progress the schedule never credited. `opened_at` is still
                  there for anything that wants raw visits. */}
              {node.readings_at?.length > 0 && (
                <span className="text-slate-600">
                  {" "}
                  · {node.readings_at.length} reading
                  {node.readings_at.length === 1 ? "" : "s"}
                </span>
              )}
            </dd>
          </div>
        )}
      </dl>

      {deletedAt && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2">
          <span className="text-xs text-rose-300">
            Deleted {formatStamp(deletedAt)} — hidden from the graph, the table
            and recommendations. Nothing was destroyed.
          </span>
          <button
            type="button"
            onClick={restoreNode}
            disabled={deleting}
            className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-1 text-xs text-slate-200 transition enabled:hover:border-sky-500/40 enabled:hover:text-sky-300 disabled:opacity-50"
          >
            Restore
          </button>
        </div>
      )}

      <div className={`${PROSE} mt-6 ${compact ? "" : "lg:prose-lg"}`}>
        <ReactMarkdown
          remarkPlugins={MATH_REMARK}
          rehypePlugins={MATH_REHYPE}
          components={components}
        >
          {body}
        </ReactMarkdown>
      </div>

      <section className="mt-6 divide-y divide-slate-800/70 rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-2">
        {VOTE_FIELDS.map((field) => (
          <VoteRow
            key={field.key}
            field={field}
            pressed={pressed[field.key]}
            pending={pending === field.key}
            failed={failed === field.key}
            onVote={castVote}
          />
        ))}
      </section>

      {/* Delete on the left, Prev/Next on the right: the two things you do when
          you have finished reading, kept apart so neither is hit by accident. */}
      {(nav || !deletedAt) && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          {!deletedAt && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-1 text-xs text-slate-500 transition hover:border-rose-500/40 hover:text-rose-300"
            >
              Delete node
            </button>
          )}
          <div className="ml-auto min-w-0">{nav}</div>
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        title={`Delete "${node.title}"?`}
        confirmLabel="Delete"
        busy={deleting}
        onCancel={() => setConfirming(false)}
        onConfirm={removeNode}
      >
        <p>
          It will be hidden from the drawing, the node table and every
          recommendation.
        </p>
        <p className="mt-2">
          <strong className="text-slate-300">Nothing is destroyed.</strong> The
          markdown file stays where it is, and so do this node&apos;s readings
          and votes. You can restore it from this page afterwards.
        </p>
        {node.dependents?.length > 0 && (
          <p className="mt-2 text-amber-300">
            {node.dependents.length} node
            {node.dependents.length === 1 ? " is" : "s are"} built on this one
            and will lose the connection while it is hidden.
          </p>
        )}
      </ConfirmDialog>

      <div className={`mt-6 grid grid-cols-1 gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <EdgeList
          title="Built from"
          empty="Nothing — this is a starting point."
          nodes={node.dependencies}
          onSelect={onSelect}
        />
        <EdgeList
          title="Built on this"
          empty="Nothing yet."
          nodes={node.dependents}
          onSelect={onSelect}
        />
      </div>
    </article>
  );
}
